import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Toast from '@radix-ui/react-toast';
import {
  CheckIcon,
  CrossCircledIcon,
  ExclamationTriangleIcon,
  InfoCircledIcon,
  Cross2Icon,
  ChevronDownIcon,
} from '@radix-ui/react-icons';
import { ANNOUNCE } from './toastStore';

// Exit animation budget. The store entry outlives Radix's `data-state=closed`
// by this much so the slide-out actually plays before the node unmounts.
const EXIT_MS = 200;

const ICONS = {
  success: CheckIcon,
  error: CrossCircledIcon,
  warning: ExclamationTriangleIcon,
  info: InfoCircledIcon,
};

/**
 * Severity lives in one tinted icon chip rather than a coloured card or a left
 * rail — the same "tinted pill carries the state" move GapStatusPill makes, so
 * a toast reads as part of the same system. The card itself stays neutral.
 */
const CHIP = {
  success: 'bg-primaryColor/10 text-primaryColor',
  error: 'bg-red-50 text-red-600',
  warning: 'bg-amber-50 text-amber-600',
  info: 'bg-slate-100 text-slate-500',
};

const PROGRESS = {
  success: 'bg-primaryColor/40',
  error: 'bg-red-500/40',
  warning: 'bg-amber-500/40',
  info: 'bg-slate-400/40',
};

// Two stacked shadows: a tight contact shadow keeps the hairline crisp, a wide
// soft one lifts the card off the page. `shadow-lg` alone reads flat at this size.
const CARD_SHADOW = '0 1px 2px rgba(16, 24, 40, 0.04), 0 8px 24px -6px rgba(16, 24, 40, 0.14)';

/**
 * One toast.

 */
export default function CgToast({ toast, onDismiss }) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const exitTimer = useRef(null);

  useEffect(() => () => clearTimeout(exitTimer.current), []);

  const close = () => {
    setOpen(false);
    clearTimeout(exitTimer.current);
    exitTimer.current = setTimeout(() => onDismiss(toast.id), EXIT_MS);
  };

  const Icon = ICONS[toast.variant] ?? InfoCircledIcon;
  const hasDetails = Array.isArray(toast.details) && toast.details.length > 0;
  // Expanding sets duration to Infinity, so there is no longer a countdown to draw.
  const showProgress = !expanded && Number.isFinite(toast.duration);

  return (
    <Toast.Root
      open={open}
      // An expanded issue list must not vanish mid-read.
      duration={expanded ? Infinity : toast.duration}
      type={ANNOUNCE[toast.variant] ?? 'background'}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      data-testid="cg-toast"
      data-variant={toast.variant}
      style={{ boxShadow: CARD_SHADOW }}
      className={[
        'group relative overflow-hidden',
        'w-[380px] max-w-[calc(100vw-32px)] bg-white rounded-xl border border-slate-200',
        'pl-3 pr-2.5 py-[11px]',
        // Enter and exit both travel along the same axis as the viewport edge,
        // so the stack reads as sliding in and out of the right gutter.
        'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-right-4',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-right-4',
        'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0',
        'data-[swipe=end]:animate-out data-[swipe=end]:fade-out-0 data-[swipe=end]:slide-out-to-right-4',
        'duration-200 ease-out motion-reduce:animate-none',
      ].join(' ')}
    >
      <div className="flex items-start gap-2.5">
        <span
          aria-hidden
          className={`mt-[1px] grid h-[22px] w-[22px] shrink-0 place-items-center rounded-lg ${
            CHIP[toast.variant] ?? CHIP.info
          }`}
        >
          <Icon className="h-[13px] w-[13px]" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Toast.Title className="m-0 truncate text-[13px] font-semibold leading-[18px] text-slate-900">
              {toast.title}
            </Toast.Title>
            {toast.count > 1 ? (
              <span
                data-testid="cg-toast-count"
                className="shrink-0 rounded-md bg-slate-100 px-[5px] py-[1px] text-[10px] font-semibold tabular-nums text-slate-500"
              >
                {/* `times`, not `count`: a `count` option makes i18next resolve
                    plural forms, and the extractor then demands _one/_few/_many
                    variants of what is only a numeric badge. */}
                {t('common:toastChrome.repeated', '×{{times}}', { times: toast.count })}
              </span>
            ) : null}
          </div>

          {toast.description ? (
            <Toast.Description className="m-0 mt-[3px] whitespace-pre-line break-words text-[12px] leading-[17px] text-slate-500">
              {toast.description}
            </Toast.Description>
          ) : null}

          {hasDetails ? (
            <>
              <button
                type="button"
                data-testid="cg-toast-expander"
                aria-expanded={expanded}
                onClick={() => setExpanded((value) => !value)}
                className="mt-[7px] inline-flex items-center gap-[3px] rounded text-[11px] font-semibold text-slate-500 transition-colors hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40"
              >
                {expanded
                  ? t('common:toastChrome.showLess', 'Show less')
                  : t('common:toastChrome.moreIssues', '+{{count}} more', {
                      count: toast.details.length,
                    })}
                <ChevronDownIcon
                  className={`h-[11px] w-[11px] transition-transform duration-150 ${
                    expanded ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {expanded ? (
                <ul className="m-0 mt-[7px] max-h-40 list-none space-y-[5px] overflow-y-auto border-t border-slate-100 pl-0 pt-[7px]">
                  {toast.details.map((detail, index) => (
                    <li
                      // Issue lines have no stable id; order is the identity.
                      key={index}
                      className="flex items-start gap-[7px] text-[11px] leading-[16px] text-slate-500"
                    >
                      <span
                        aria-hidden
                        className="mt-1.5 h-[3px] w-[3px] shrink-0 rounded-full bg-slate-300"
                      />
                      <span className="break-words">{detail}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          ) : null}

          {toast.action ? (
            <Toast.Action
              asChild
              altText={toast.action.label}
            >
              <button
                type="button"
                onClick={toast.action.onClick}
                className="mt-[9px] inline-flex items-center rounded-lg bg-primaryColor px-2.5 py-1 text-[11px] font-semibold text-white transition hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40"
              >
                {toast.action.label}
              </button>
            </Toast.Action>
          ) : null}
        </div>

        <Toast.Close
          aria-label={t('common:actions.close', 'Close')}
          className="-mr-0.5 -mt-[1px] shrink-0 rounded-md p-[3px] text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40"
        >
          <Cross2Icon className="h-[13px] w-[13px]" />
        </Toast.Close>
      </div>

      {showProgress ? (
        <span
          aria-hidden
          data-testid="cg-toast-progress"
          // Paused whenever the viewport is hovered or focused, which is
          // exactly when Radix pauses the dismiss timer — the bar would
          // otherwise drain to zero while the toast sat there.
          className={`animate-toastProgress absolute inset-x-0 bottom-0 h-[2px] origin-left ${
            PROGRESS[toast.variant] ?? PROGRESS.info
          } motion-reduce:hidden group-hover/viewport:[animation-play-state:paused] group-focus-within/viewport:[animation-play-state:paused]`}
          style={{ animationDuration: `${toast.duration}ms` }}
        />
      ) : null}
    </Toast.Root>
  );
}
